import React from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ClipboardCheck, BadgeCheck, SlidersHorizontal } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  useReportControls, ColumnHeader, SearchDrawer, ColumnsToggle, rowMatchesFilters, applySort, type ColumnConfig,
} from '@/components/shared/report-controls';
import { fmtDate, fmtDateTime } from '@/lib/format-date';

interface Row {
  assetId: string;
  fleetNumber: string;
  /** Numeric prefix of fleetNumber — sort key so "2" sorts before "11". */
  fleetNumberSort?: number | null;
  registrationNumber?: string | null;
  make?: string | null;
  model?: string | null;
  expiryDate: string;
  daysFromToday: number;
  dateOfIssue?: string;
  licencePeriod?: number;
  nztaReference?: string | null;
  wofCof?: string | null;
  testingDate?: string | null;
  kind?: string;
}

type ReportKind = 'rego' | 'wof' | 'cof';

const META: Record<ReportKind, { title: string; endpoint: string; icon: React.ComponentType<{ className?: string }>; query?: Record<string, string> }> = {
  rego: { title: 'Registration Reminders', endpoint: '/dashboard/rego-reminders', icon: BadgeCheck },
  wof:  { title: 'WoF Reminders',          endpoint: '/dashboard/wof-reminders',  icon: ClipboardCheck, query: { type: 'WOF' } },
  cof:  { title: 'CoF Reminders',          endpoint: '/dashboard/wof-reminders',  icon: ClipboardCheck, query: { type: 'COF' } },
};

/** Walk a dotted key path (e.g. "asset.fleetNumber") on a plain object. */
function getPath(obj: any, path: string): any {
  return path.split('.').reduce((a: any, k) => (a == null ? a : a[k]), obj);
}

export function ReminderReportPage({ kind }: { kind: ReportKind }) {
  const [params] = useSearchParams();
  const status = (params.get('status') ?? 'overdue') as 'overdue' | 'due';
  // Optional service-location scope, carried from the dashboard card. Kept in
  // the URL so the status toggle below preserves it and the backend filters by
  // the asset's serviceLocation.
  const location = params.get('location') ?? '';
  const statusQs = (s: 'overdue' | 'due') => `?status=${s}${location ? `&location=${encodeURIComponent(location)}` : ''}`;
  const meta = META[kind];

  // Resolve the location name for the scope banner (only when scoped).
  const { data: locations = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['/master-data/service-locations'],
    queryFn: async () => (await api<Array<{ id: string; name: string }>>('/master-data/service-locations?limit=500')).data || [],
    staleTime: 5 * 60_000,
    enabled: !!location,
  });
  const locName = locations.find((l) => l.id === location)?.name;

  // Column set depends on kind — Registration has Issued + NZTA Ref columns,
  // WoF/CoF has a Tested column instead. The rest are common.
  const columns: ColumnConfig[] = React.useMemo(() => {
    const base: ColumnConfig[] = [
      // Sort on the numeric prefix so Fleet # orders 1,2,3,…,11,101 — not the
      // lexicographic 1,11,101,2. Filtering still matches the displayed string.
      { key: 'fleetNumber', label: 'Fleet #', filterType: 'text', sortField: 'fleetNumberSort' },
      { key: 'registrationNumber', label: 'Rego', filterType: 'text',
        render: (r: any) => r.registrationNumber ?? '—' },
      { key: 'makeModel', label: 'Make / Model', filterType: 'text',
        sortField: 'make',
        filterField: 'makeModel',
        render: (r: any) => <span className="text-muted-foreground">{[r.make, r.model].filter(Boolean).join(' ') || '—'}</span> },
    ];
    if (kind === 'rego') {
      base.push(
        { key: 'dateOfIssue', label: 'Issued', filterType: 'date',
          render: (r: any) => fmtDate(r.dateOfIssue) },
        { key: 'nztaReference', label: 'NZTA Ref', filterType: 'text',
          render: (r: any) => <span className="font-mono text-xs">{r.nztaReference ?? '—'}</span> },
      );
    } else {
      base.push(
        { key: 'testingDate', label: 'Tested', filterType: 'date',
          render: (r: any) => fmtDate(r.testingDate) },
      );
    }
    base.push(
      { key: 'expiryDate', label: 'Expires', filterType: 'date',
        render: (r: any) => fmtDate(r.expiryDate) },
      { key: 'daysFromToday', label: 'Days', filterType: 'number',
        render: (r: any) => r.daysFromToday < 0
          ? <Badge variant="destructive">{-r.daysFromToday}d overdue</Badge>
          : <Badge variant="secondary">in {r.daysFromToday}d</Badge> },
    );
    return base;
  }, [kind]);

  const ctl = useReportControls({ namespace: `/compliance-reminders/${kind}`, columns });

  // The aggregated endpoint doesn't accept the shell's `?<field>_contains=`
  // style filters, so we fetch the unfiltered list and apply filters + sort
  // client-side. Cheap since the dataset is bounded (one row per asset).
  const qs = new URLSearchParams({ status, ...(location ? { location } : {}), ...(meta.query ?? {}) }).toString();
  const { data: allRows = [], isLoading } = useQuery<Row[]>({
    queryKey: [meta.endpoint, qs],
    queryFn: async () => (await api<Row[]>(`${meta.endpoint}?${qs}`)).data || [],
  });

  // Project the synthetic `makeModel` field so the text column filter has
  // something concrete to match against (the row only has separate `make`
  // and `model`).
  const projected = React.useMemo(() => allRows.map((r) => ({ ...r, makeModel: [r.make, r.model].filter(Boolean).join(' ') })), [allRows]);
  const filtered = React.useMemo(() => projected.filter((r) => rowMatchesFilters(r, columns, ctl.filters)), [projected, columns, ctl.filters]);
  const sorted = React.useMemo(() => applySort(filtered, ctl.sortState), [filtered, ctl.sortState]);

  const Icon = meta.icon;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard"><ChevronLeft className="h-4 w-4" /> Back to Dashboard</Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Icon className="h-5 w-5" /> {meta.title}
        </h1>
        <p className="text-sm text-muted-foreground">
          Showing <strong>{status === 'overdue' ? 'Overdue' : 'Due Soon (next 30 days)'}</strong> records · one row per asset (latest renewal).
        </p>
        {location && (
          <p className="text-xs text-muted-foreground mt-1">
            Filtered to <strong className="text-foreground">{locName ?? 'selected location'}</strong>
            {' · '}
            <Link to={`?status=${status}`} className="underline hover:text-foreground">show all locations</Link>
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <Link to={statusQs('overdue')}>
          <Button size="sm" variant={status === 'overdue' ? 'default' : 'outline'}>
            <AlertTriangle className="h-3.5 w-3.5" /> Overdue
          </Button>
        </Link>
        <Link to={statusQs('due')}>
          <Button size="sm" variant={status === 'due' ? 'default' : 'outline'}>
            Due Soon
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {ctl.activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={() => ctl.setFilters({})} className="text-xs">
            Clear filters ({ctl.activeFilterCount})
          </Button>
        )}
        {ctl.hasNonDefaultView && (
          <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={ctl.resetView}>
            Reset view
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => ctl.setDrawerOpen(true)}>
          <SlidersHorizontal className="h-4 w-4 mr-1" /> Search
        </Button>
        <ColumnsToggle ctl={ctl} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{sorted.length} record(s){sorted.length !== allRows.length && <span className="text-xs text-muted-foreground font-normal ml-2">of {allRows.length}</span>}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {allRows.length === 0
                ? (status === 'overdue' ? 'Nothing overdue — clean.' : 'Nothing due in the next 30 days.')
                : 'No records match the current filters.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {ctl.visibleColumns.map((col) => (
                    <TableHead key={col.key} className="p-0">
                      <ColumnHeader {...ctl.headerProps(col)} />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r) => (
                  <TableRow key={r.assetId}>
                    {ctl.visibleColumns.map((col) => (
                      <TableCell key={col.key} className={col.key === 'fleetNumber' ? 'font-medium' : ''}>
                        {col.render ? col.render(r) : (getPath(r, col.key) ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {ctl.drawerOpen && <SearchDrawer {...ctl.drawerProps} />}
    </div>
  );
}

export const RegistrationRemindersPage = () => <ReminderReportPage kind="rego" />;
export const WofRemindersPage          = () => <ReminderReportPage kind="wof" />;
export const CofRemindersPage          = () => <ReminderReportPage kind="cof" />;
